/**
 * ListingForm — authoring form for the `listing` archetype.
 *
 * Produces:
 *   { record, columns: [field..], filters: [{field, operator, value}],
 *     sort: [{field, direction}], detail?: { child, columns: [col..] } }
 *
 * Catalog-driven: records from GET /catalog/records; fields (with per-field
 * operators) from GET /catalog/records/:record/fields; header-detail children
 * from GET /catalog/detail-children/:record.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Icons } from '../../common/Icons';
import { reportCatalogService } from '../../../services/api';

const ListingForm = ({ spec, onChange, targetTenantId }) => {
  const [records, setRecords] = useState([]);
  const [fields, setFields] = useState([]);
  const [children, setChildren] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await reportCatalogService.getRecords(targetTenantId);
        setRecords(res.data || []);
      } catch (e) { setErr(e.response?.data?.message || 'Failed to load records.'); }
    })();
  }, [targetTenantId]);

  useEffect(() => {
    if (!spec.record) { setFields([]); setChildren([]); return; }
    (async () => {
      try {
        const [f, c] = await Promise.all([
          reportCatalogService.getFields(targetTenantId, spec.record),
          reportCatalogService.getDetailChildren(targetTenantId, spec.record),
        ]);
        setFields(f.data || []);
        setChildren(c.data || []);
      } catch (e) { setErr('Failed to load fields for this record.'); }
    })();
  }, [spec.record, targetTenantId]);

  const patch = useCallback((p) => onChange({ ...spec, ...p }), [spec, onChange]);

  const selectRecord = (record) => {
    onChange({ record, columns: [], filters: [], sort: [] });
  };

  const filterableFields = fields.filter((f) => f.is_filterable);
  const sortableFields = fields.filter((f) => f.is_sortable);
  const fieldByName = (n) => fields.find((f) => f.field_name === n);

  const toggleColumn = (name) => {
    const cur = spec.columns || [];
    const next = cur.includes(name) ? cur.filter((c) => c !== name) : [...cur, name];
    patch({ columns: next });
  };

  // Filters
  const addFilter = () => patch({ filters: [...(spec.filters || []), { field: '', operator: '', value: '' }] });
  const updateFilter = (i, p) => {
    const next = [...(spec.filters || [])];
    next[i] = { ...next[i], ...p };
    patch({ filters: next });
  };
  const removeFilter = (i) => patch({ filters: (spec.filters || []).filter((_, idx) => idx !== i) });

  // Sort
  const addSort = () => patch({ sort: [...(spec.sort || []), { field: '', direction: 'asc' }] });
  const updateSort = (i, p) => {
    const next = [...(spec.sort || [])];
    next[i] = { ...next[i], ...p };
    patch({ sort: next });
  };
  const removeSort = (i) => patch({ sort: (spec.sort || []).filter((_, idx) => idx !== i) });

  const selectedCols = new Set(spec.columns || []);
  const detailChild = children.find((c) => c.child === spec.detail?.child);

  return (
    <div>
      {err && <div className="alert alert-warning mb-3"><Icons.AlertCircle size={18} /><span>{err}</span></div>}

      <Field label="Record">
        <select className="form-control" value={spec.record || ''} onChange={(e) => selectRecord(e.target.value)}>
          <option value="">Select a record…</option>
          {records.map((r) => <option key={r.record_name} value={r.record_name}>{r.display_name} ({r.category})</option>)}
        </select>
      </Field>

      {spec.record && fields.length > 0 && (
        <>
          <Field label="Columns">
            <CheckGrid>
              {fields.map((f) => (
                <CheckItem key={f.field_name} checked={selectedCols.has(f.field_name)} onChange={() => toggleColumn(f.field_name)} label={f.display_name} />
              ))}
            </CheckGrid>
          </Field>

          <Field label="Filters">
            {(spec.filters || []).map((flt, i) => {
              const fld = fieldByName(flt.field);
              const ops = fld ? fld.operators : [];
              const op = ops.find((o) => o.value === flt.operator);
              const arity = op ? op.arity : 'one';
              return (
                <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <select className="form-control" style={{ width: '180px' }} value={flt.field}
                    onChange={(e) => updateFilter(i, { field: e.target.value, operator: '', value: '' })}>
                    <option value="">Field…</option>
                    {filterableFields.map((f) => <option key={f.field_name} value={f.field_name}>{f.display_name}</option>)}
                  </select>
                  <select className="form-control" style={{ width: '160px' }} value={flt.operator}
                    onChange={(e) => updateFilter(i, { operator: e.target.value })} disabled={!flt.field}>
                    <option value="">Operator…</option>
                    {ops.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  {arity === 'none' ? null : arity === 'two' ? (
                    <>
                      <input className="form-control" style={{ width: '120px' }} placeholder="from"
                        value={Array.isArray(flt.value) ? (flt.value[0] || '') : ''}
                        onChange={(e) => updateFilter(i, { value: [e.target.value, Array.isArray(flt.value) ? (flt.value[1] || '') : ''] })} />
                      <input className="form-control" style={{ width: '120px' }} placeholder="to"
                        value={Array.isArray(flt.value) ? (flt.value[1] || '') : ''}
                        onChange={(e) => updateFilter(i, { value: [Array.isArray(flt.value) ? (flt.value[0] || '') : '', e.target.value] })} />
                    </>
                  ) : arity === 'list' ? (
                    <input className="form-control" style={{ flex: 1, minWidth: '160px' }} placeholder="comma-separated values"
                      value={Array.isArray(flt.value) ? flt.value.join(',') : ''}
                      onChange={(e) => updateFilter(i, { value: e.target.value.split(',').map((v) => v.trim()).filter(Boolean) })} />
                  ) : (
                    <input className="form-control" style={{ flex: 1, minWidth: '160px' }} placeholder="value"
                      value={flt.value ?? ''} onChange={(e) => updateFilter(i, { value: e.target.value })} />
                  )}
                  <button className="btn btn-secondary btn-sm" onClick={() => removeFilter(i)} title="Remove"><Icons.X size={14} /></button>
                </div>
              );
            })}
            <button className="btn btn-secondary btn-sm" onClick={addFilter}><Icons.Plus size={14} /> Add filter</button>
          </Field>

          <Field label="Sort">
            {(spec.sort || []).map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                <select className="form-control" style={{ width: '180px' }} value={s.field}
                  onChange={(e) => updateSort(i, { field: e.target.value })}>
                  <option value="">Field…</option>
                  {sortableFields.map((f) => <option key={f.field_name} value={f.field_name}>{f.display_name}</option>)}
                </select>
                <select className="form-control" style={{ width: '140px' }} value={s.direction}
                  onChange={(e) => updateSort(i, { direction: e.target.value })}>
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </select>
                <button className="btn btn-secondary btn-sm" onClick={() => removeSort(i)} title="Remove"><Icons.X size={14} /></button>
              </div>
            ))}
            <button className="btn btn-secondary btn-sm" onClick={addSort}><Icons.Plus size={14} /> Add sort</button>
          </Field>

          {children.length > 0 && (
            <Field label="Header-detail (optional)">
              <select className="form-control" value={spec.detail?.child || ''}
                onChange={(e) => {
                  if (!e.target.value) { const { detail, ...rest } = spec; onChange(rest); }
                  else patch({ detail: { child: e.target.value } });
                }}>
                <option value="">None</option>
                {children.map((c) => <option key={c.child} value={c.child}>{c.child}</option>)}
              </select>
              {detailChild && (
                <div style={{ marginTop: '8px' }}>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '6px' }}>Detail columns (default: all)</div>
                  <CheckGrid>
                    {detailChild.projection.map((p) => {
                      const selected = (spec.detail?.columns || []).includes(p.col);
                      return (
                        <CheckItem key={p.col} checked={selected} label={p.label}
                          onChange={() => {
                            const cur = spec.detail?.columns || [];
                            const next = cur.includes(p.col) ? cur.filter((x) => x !== p.col) : [...cur, p.col];
                            patch({ detail: { ...spec.detail, columns: next } });
                          }} />
                      );
                    })}
                  </CheckGrid>
                </div>
              )}
            </Field>
          )}
        </>
      )}
    </div>
  );
};

const Field = ({ label, children }) => (
  <div style={{ marginBottom: '16px' }}>
    {label ? <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--text-muted)' }}>{label}</label> : null}
    {children}
  </div>
);
const CheckGrid = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '6px' }}>{children}</div>
);
const CheckItem = ({ checked, onChange, label }) => (
  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
    <input type="checkbox" checked={checked} onChange={onChange} /><span>{label}</span>
  </label>
);

export default ListingForm;
